import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowUpRightIcon } from 'lucide-react';
import { AmbientCanvas, type Backdrop } from '../components/AmbientCanvas';
import { BrandMark } from '../components/BrandMark';
import { PanFlipScene } from '../components/PanFlipScene';
import { riseItem, spring, staggerContainer } from '../utils/motion';
const GRAIN = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)'/%3E%3C/svg%3E\")";
const HEADLINE = ['We’re', 'cookin’'];
interface ComingSoonProps {
  backdrop: Backdrop;
  palette: 'warm' | 'night';
}
export function ComingSoon({
  backdrop,
  palette
}: ComingSoonProps) {
  const reduce = useReducedMotion();
  const item = riseItem(reduce, 14);
  const tint = backdrop === 'embers' ? palette === 'night' ? '235 178 90' : '232 163 61' : palette === 'night' ? '235 132 82' : '194 78 24';
  return <div className={`relative min-h-screen w-full overflow-hidden bg-canvas ${palette === 'night' ? 'palette-night' : ''}`} data-id="element-47" data-mp="125w3uirkpv5v">
      {backdrop !== 'none' ? <AmbientCanvas mode={backdrop} tint={tint} data-id="element-48" data-mp="1b61cew8i6zlm" /> : null}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: GRAIN,
      mixBlendMode: palette === 'night' ? 'screen' : 'multiply'
    }} data-id="element-49" data-mp="1b3et5zc92vnk" />

      <div className="relative mx-auto flex min-h-screen max-w-shell flex-col px-6 py-8 lg:px-10 lg:py-10" data-id="element-50" data-mp="1pvqgaz7zdhpl">
        <motion.header initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.22,
        ease: 'easeOut'
      }} className="flex items-center gap-2.5" data-id="element-51" data-mp="1tuz9a1v75bk3">
          <BrandMark className="h-8 w-8 text-action" data-id="element-52" data-mp="23uyh1wjskxvw" />
          <span className="font-display text-[19px] font-semibold tracking-[-0.02em] text-ink" data-id="element-53" data-mp="3rxkfnl8m7yt">
            Pan &amp; Apron
          </span>
        </motion.header>

        <motion.main variants={staggerContainer(0.06, 0.08)} initial="hidden" animate="show" className="grid flex-1 items-center gap-10 py-14 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-14 lg:py-6" data-id="element-54" data-mp="3uwfaxtcik4cv">
          <div data-id="element-55" data-mp="mhng0cwtqxrs">
            <h1 className="font-display text-[clamp(3rem,7.5vw,6rem)] font-semibold leading-[0.98] tracking-[-0.04em] text-ink" aria-label="We’re cookin’" data-id="element-56" data-mp="mhdscvz952p9">
              {HEADLINE.map((word, i) => <span key={word} className="inline-block overflow-hidden pb-2 align-bottom" data-id="element-57" data-mp="rhryg8ham2kn" data-mpi={i}>
                  <motion.span variants={reduce ? {
                hidden: {
                  opacity: 0
                },
                show: {
                  opacity: 1,
                  transition: {
                    duration: 0.15
                  }
                }
              } : {
                hidden: {
                  opacity: 0,
                  y: '60%'
                },
                show: {
                  opacity: 1,
                  y: 0,
                  transition: spring.smooth
                }
              }} className="inline-block" data-id="element-58" data-mp="rfyn4fvqiu6b">
                    {word}
                    {i < HEADLINE.length - 1 ? '\u00A0' : ''}
                  </motion.span>
                </span>)}
            </h1>

            <motion.p variants={item} className="mt-5 max-w-[42ch] text-[19px] font-medium leading-snug text-ink" data-id="element-59" data-mp="13ykikrnafvww">
              Private chefs for hire, all in one place. The app is still on the heat —
              low and slow, nearly plated.
            </motion.p>

            <motion.p variants={item} className="mt-3 max-w-[42ch] text-[16px] leading-relaxed text-ink-2" data-id="element-60" data-mp="g3htcikknqfw">
              Want in early, or cook for a living and fancy a spot on the roster? Knock
              on the kitchen door.
            </motion.p>

            <motion.div variants={item} className="mt-8" data-id="element-61" data-mp="2ffkavn9myaqx">
              <motion.a href="mailto:info@panandapron.com" whileHover={reduce ? undefined : {
              scale: 1.02
            }} whileTap={reduce ? undefined : {
              scale: 0.97
            }} transition={spring.snappy} className="inline-flex items-center gap-2 rounded-full bg-action px-6 py-3.5 text-[16px] font-medium text-on-action transition-colors duration-150 ease-out hover:bg-ink hover:text-canvas" data-id="element-62" data-mp="2ff0uwc3jpwfj">
                info@panandapron.com
                <ArrowUpRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-63" data-mp="3sl4d1uq2jqft" />
              </motion.a>
            </motion.div>
          </div>

          <motion.div variants={reduce ? {
          hidden: {
            opacity: 0
          },
          show: {
            opacity: 1,
            transition: {
              duration: 0.15
            }
          }
        } : {
          hidden: {
            opacity: 0,
            scale: 0.96
          },
          show: {
            opacity: 1,
            scale: 1,
            transition: spring.heavy
          }
        }} className="justify-self-center lg:justify-self-end" data-id="element-64" data-mp="3jr9j7iuedmfh">
            <PanFlipScene className="h-auto w-full max-w-[560px]" data-id="element-65" data-mp="34wbnqh3ycgu6" />
          </motion.div>
        </motion.main>

        <motion.footer initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.22,
        delay: 0.2,
        ease: 'easeOut'
      }} className="text-[12px] text-ink-2" data-id="element-66" data-mp="2uso620bd8gww">
          © 2026 Pan and Apron
        </motion.footer>
      </div>
    </div>;
}